import React from 'react';
import {
  Box,
  Grid,
  TextField,
  Typography,
} from '@mui/material';
import { MuiTelInput,type MuiTelInputInfo } from 'mui-tel-input';

import { type EmergencyContact } from '../../types/types';

interface EmergencyContactCardProps {
  contact: EmergencyContact | null | undefined;
  onChange: (contact: EmergencyContact | null) => void;
  onBlur?: () => void;
  errors?: Record<string, string>;
  highlightedFields?: string[];
  disabled?: boolean;
}

const EmergencyContactCard: React.FC<EmergencyContactCardProps> = ({
  contact,
  onChange,
  onBlur,
  errors = {},
  highlightedFields = [],
  disabled = false,
}) => {
  const handleFieldChange = (field: keyof EmergencyContact, value: string) => {
    const updatedContact: EmergencyContact = {
      ...(contact || {}),
      [field]: value || null,
    };
    onChange(updatedContact);
  };

  // MuiTelInput's onChange hands back an `info` object whose `numberValue` is
  // already the library's own E.164 parse — using it (rather than reconstructing
  // E.164 by stripping digits and assuming a US country code) is required for
  // `onlyCountries` to actually work: a GB number like "+44 7946 123456" would
  // otherwise get mangled into "+1447946123456". Falls back to the raw value
  // when the library has no parse for the input. Same approach as
  // PR #2837 (CUS-1461) for the PatientInfoModal emergency contact phone.
  const handlePhoneChange = (newValue: string, info: MuiTelInputInfo) => {
    handleFieldChange('phone', info.numberValue ?? newValue);
  };

  const formatPhoneDisplay = (phone: string | null | undefined): string => {
    if (!phone) return '';
    // If it's already in E.164, mui-tel-input will format it nicely
    return phone;
  };

  return (
    <Box>
      <Typography variant="subtitle1" sx={{ fontWeight: 500, mb: 2 }}>
        Emergency Contact
      </Typography>
      
      <Grid container spacing={2}>
        <Grid size={{ xs: 12, sm: 6 }}>
          <TextField
            label="Contact Name"
            value={contact?.name || ''}
            onChange={(e) => handleFieldChange('name', e.target.value)}
            onBlur={onBlur}
            variant="outlined"
            size="small"
            fullWidth
            disabled={disabled}
            error={!!errors.name}
            helperText={errors.name}
            sx={{
              ...(highlightedFields.includes('emergency_contact_name') && {
                '& .MuiOutlinedInput-root': {
                  '& fieldset': {
                    borderColor: 'primary.main',
                    borderWidth: 2,
                  },
                },
              }),
            }}
          />
        </Grid>
        
        <Grid size={{ xs: 12, sm: 6 }}>
          <TextField
            label="Relationship"
            value={contact?.relationship || ''}
            onChange={(e) => handleFieldChange('relationship', e.target.value)}
            onBlur={onBlur}
            variant="outlined"
            size="small"
            fullWidth
            disabled={disabled}
            error={!!errors.relationship}
            helperText={errors.relationship || 'e.g., Spouse, Parent, Sibling, Friend'}
            sx={{
              ...(highlightedFields.includes('emergency_contact_relationship') && {
                '& .MuiOutlinedInput-root': {
                  '& fieldset': {
                    borderColor: 'primary.main',
                    borderWidth: 2,
                  },
                },
              }),
            }}
          />
        </Grid>
        
        <Grid size={{ xs: 12, sm: 6 }}>
          <MuiTelInput
            label="Contact Phone"
            value={formatPhoneDisplay(contact?.phone)}
            onChange={handlePhoneChange}
            onBlur={onBlur}
            defaultCountry="US"
            forceCallingCode
            onlyCountries={['US', 'CA', 'GB', 'AU', 'NZ', 'ZA', 'MX']}
            size="small"
            disabled={disabled}
            error={!!errors.phone}
            helperText={errors.phone}
            sx={{
              width: '100%',
              '& input': {
                height: '1.4375em',
              },
              ...(highlightedFields.includes('emergency_contact_phone') && {
                '& .MuiOutlinedInput-root': {
                  '& fieldset': {
                    borderColor: 'primary.main',
                    borderWidth: 2,
                  },
                },
              }),
            }}
          />
        </Grid>
        
        <Grid size={{ xs: 12, sm: 6 }}>
          <TextField
            label="Contact Email"
            type="email"
            value={contact?.email || ''}
            onChange={(e) => handleFieldChange('email', e.target.value)}
            onBlur={onBlur}
            variant="outlined"
            size="small"
            fullWidth
            disabled={disabled}
            error={!!errors.email}
            helperText={errors.email}
            sx={{
              ...(highlightedFields.includes('emergency_contact_email') && {
                '& .MuiOutlinedInput-root': {
                  '& fieldset': {
                    borderColor: 'primary.main',
                    borderWidth: 2,
                  },
                },
              }),
            }}
          />
        </Grid>
      </Grid>
    </Box>
  );
};

export default EmergencyContactCard;